import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronLeft, Moon, Sparkles, RefreshCw, Eye, Loader2, Flame, Droplets, Wind, Mountain, Crown, Lock, ArrowRight } from 'lucide-react';
import { useAuth } from '@/components/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUITS = {
  wands: { name: 'Bâtons', icon: Flame, color: 'orange', bgColor: 'bg-orange-500' },
  cups: { name: 'Coupes', icon: Droplets, color: 'blue', bgColor: 'bg-blue-500' },
  swords: { name: 'Épées', icon: Wind, color: 'gray', bgColor: 'bg-gray-500' },
  pentacles: { name: 'Deniers', icon: Mountain, color: 'green', bgColor: 'bg-emerald-500' }
};

// Premium Upsell Component
const PremiumUpsell = ({ onNavigate }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    className="max-w-md mx-auto"
  >
    <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-3xl border-2 border-purple-200 p-8 text-center shadow-xl">
      <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full mb-6 shadow-lg">
        <Crown className="h-10 w-10 text-white" />
      </div>
      
      <h2 className="text-2xl font-bold text-gray-900 mb-3">
        Fonctionnalité Premium
      </h2>
      
      <p className="text-gray-600 mb-6 max-w-sm mx-auto">
        La Carte du Jour est réservée aux membres Premium MYSTERIAE ou REVELIAE Intégral.
      </p>
      
      <div className="space-y-3 mb-8 text-left">
        {[
          "Tirage quotidien personnalisé",
          "Interprétation IA approfondie",
          "Historique de vos tirages",
          "Accès aux 78 arcanes"
        ].map((feature, i) => (
          <div key={i} className="flex items-center gap-3 p-2 bg-white/60 rounded-lg">
            <Sparkles className="h-4 w-4 text-purple-500 flex-shrink-0" />
            <span className="text-sm text-gray-700">{feature}</span>
          </div>
        ))}
      </div>
      
      <Button
        onClick={() => onNavigate('/premium')}
        className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all"
        data-testid="upgrade-premium-btn"
      >
        <Crown className="h-5 w-5 mr-2" />
        Devenir Premium
        <ArrowRight className="h-5 w-5 ml-2" />
      </Button>
      
      <p className="text-xs text-gray-500 mt-4">
        À partir de 1.99€ pour 7 jours d'essai
      </p>
    </div>
  </motion.div>
);

const MysteriaeRandomPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isRevealed, setIsRevealed] = useState(false);
  const [isFlipping, setIsFlipping] = useState(false);
  const [includeMinor, setIncludeMinor] = useState(true);
  const [hasPremiumAccess, setHasPremiumAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);

  // Check premium access on mount
  useEffect(() => {
    checkPremiumAccess();
  }, [isAuthenticated]);

  const checkPremiumAccess = async () => {
    setCheckingAccess(true);
    try {
      const response = await axios.get(`${API_URL}/api/premium/check/mysteriae`, {
        withCredentials: true
      });
      setHasPremiumAccess(response.data.has_access === true);
    } catch (error) {
      console.error('Error checking premium access:', error);
      setHasPremiumAccess(false);
    } finally {
      setCheckingAccess(false);
    }
  };

  const drawCard = async () => {
    setLoading(true);
    setIsRevealed(false);
    setIsFlipping(false);
    
    try {
      const response = await fetch(`${API_URL}/api/mysteriae/random?include_minor=${includeMinor}`);
      const data = await response.json();
      setCard(data);
      
      // Start the flip animation after a short delay
      setTimeout(() => {
        setIsFlipping(true);
        setTimeout(() => {
          setIsRevealed(true);
        }, 600);
      }, 500);
    } catch (error) {
      console.error('Error fetching random card:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCardLink = () => {
    if (!card) return '/mysteriae/arcanes';
    if (card.card_type === 'major') {
      return `/mysteriae/arcanes/${card.number}`;
    }
    return `/mysteriae/arcanes/${card.card_id}`;
  };

  const getSuitInfo = () => {
    if (!card || card.card_type !== 'minor') return null;
    return SUITS[card.suit];
  };

  const suitInfo = getSuitInfo();
  const SuitIcon = suitInfo?.icon || Moon;

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 via-white to-purple-50">
      {/* Decorative elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-purple-200/20 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-purple-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link 
            to="/mysteriae" 
            className="flex items-center gap-2 text-purple-600 hover:text-purple-700 transition-colors"
            data-testid="back-to-mysteriae"
          >
            <ChevronLeft className="h-5 w-5" />
            <span className="font-semibold">MYSTERIAE</span>
          </Link>
          <div className="flex items-center gap-2">
            <Moon className="h-5 w-5 text-purple-600" />
            <span className="font-bold text-gray-900 tracking-wide">Carte du Jour</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Loading state */}
        {checkingAccess ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-purple-500 mb-4" />
            <p className="text-gray-500">Vérification de votre accès...</p>
          </div>
        ) : !hasPremiumAccess ? (
          /* Premium Upsell */
          <>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-12"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 rounded-full border border-purple-200 mb-6">
                <Lock className="h-4 w-4 text-purple-600" />
                <span className="text-purple-700 text-sm font-medium">Premium</span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Carte du Jour
              </h1>
              <p className="text-gray-600 max-w-xl mx-auto leading-relaxed">
                Tirez une carte chaque jour pour recevoir un message de réflexion et de guidance.
              </p>
            </motion.div>
            <PremiumUpsell onNavigate={navigate} />
          </>
        ) : (
          /* Premium Content */
          <>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-12"
            >
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Tirez votre carte du jour
              </h1>
              <p className="text-gray-600 max-w-xl mx-auto leading-relaxed">
                Concentrez-vous sur une question ou une intention, puis tirez une carte
                pour recevoir un message de réflexion.
              </p>
            </motion.div>

        {/* Card Display Area */}
        <div className="flex flex-col items-center">
          {!card ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center"
            >
              {/* Card back placeholder */}
              <div className="relative w-64 h-96 mb-8">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-600 to-indigo-700 rounded-2xl border-2 border-purple-300 shadow-2xl shadow-purple-200/50">
                  <div className="absolute inset-4 border-2 border-purple-400/30 rounded-xl flex items-center justify-center">
                    <div className="text-center">
                      <Moon className="h-16 w-16 text-purple-300 mx-auto mb-4" />
                      <Sparkles className="h-8 w-8 text-purple-400/50 mx-auto" />
                    </div>
                  </div>
                  {/* Corner decorations */}
                  <div className="absolute top-2 left-2 w-8 h-8 border-t-2 border-l-2 border-purple-400/40 rounded-tl-lg" />
                  <div className="absolute top-2 right-2 w-8 h-8 border-t-2 border-r-2 border-purple-400/40 rounded-tr-lg" />
                  <div className="absolute bottom-2 left-2 w-8 h-8 border-b-2 border-l-2 border-purple-400/40 rounded-bl-lg" />
                  <div className="absolute bottom-2 right-2 w-8 h-8 border-b-2 border-r-2 border-purple-400/40 rounded-br-lg" />
                </div>
              </div>

              <Button
                size="lg"
                onClick={drawCard}
                disabled={loading}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl px-8 shadow-lg"
                data-testid="draw-card-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Tirage en cours...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5 mr-2" />
                    Tirer une carte
                  </>
                )}
              </Button>

              {/* Option to include minor arcana */}
              <label className="flex items-center gap-2 text-gray-600 text-sm cursor-pointer mt-4">
                <input
                  type="checkbox"
                  checked={includeMinor}
                  onChange={(e) => setIncludeMinor(e.target.checked)}
                  className="rounded border-purple-300 text-purple-600 focus:ring-purple-500"
                />
                Inclure les Arcanes Mineurs (78 cartes)
              </label>
            </motion.div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={card.number}
                initial={{ rotateY: 180, opacity: 0 }}
                animate={{ 
                  rotateY: isFlipping ? 0 : 180, 
                  opacity: 1 
                }}
                transition={{ duration: 0.6, ease: "easeInOut" }}
                className="flex flex-col items-center"
                style={{ perspective: 1000 }}
              >
                {/* Card Image */}
                <div className="relative w-64 sm:w-72 mb-8">
                  {!isRevealed ? (
                    <div className="aspect-[2/3] bg-gradient-to-br from-purple-600 to-indigo-700 rounded-2xl border-2 border-purple-300 shadow-2xl flex items-center justify-center">
                      <Loader2 className="h-8 w-8 text-purple-300 animate-spin" />
                    </div>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <img
                        src={card.image_url}
                        alt={card.name}
                        className="w-full rounded-2xl shadow-2xl shadow-purple-200/50 border-2 border-purple-200"
                        data-testid="drawn-card-image"
                      />
                      {/* Number/Suit badge */}
                      {card.card_type === 'major' ? (
                        <div className="absolute -top-3 -left-3 w-12 h-12 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
                          <span className="text-white font-bold text-lg">{card.number}</span>
                        </div>
                      ) : (
                        <div className={`absolute -top-3 -left-3 w-12 h-12 ${suitInfo?.bgColor || 'bg-gray-500'} rounded-full flex items-center justify-center shadow-lg`}>
                          <SuitIcon className="h-6 w-6 text-white" />
                        </div>
                      )}
                    </motion.div>
                  )}
                </div>

                {/* Card Info */}
                {isRevealed && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-center max-w-md"
                  >
                    <h2 className="text-2xl font-bold text-gray-900 mb-1" data-testid="drawn-card-name">
                      {card.name}
                    </h2>
                    <p className="text-purple-600 font-medium mb-4">{card.name_en}</p>

                    {/* Keywords */}
                    <div className="flex flex-wrap justify-center gap-2 mb-6">
                      {card.keywords.slice(0, 4).map((keyword, i) => (
                        <span
                          key={i}
                          className="px-3 py-1 bg-purple-100 text-purple-700 text-sm font-medium rounded-full"
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>

                    {/* Advice */}
                    <div className="p-5 bg-white rounded-xl border border-purple-200 shadow-sm mb-6">
                      <p className="text-gray-700 italic leading-relaxed">"{card.advice}"</p>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Button
                        variant="outline"
                        onClick={drawCard}
                        className="border-purple-200 text-purple-700 hover:bg-purple-50 rounded-xl"
                        data-testid="draw-again-btn"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Nouveau tirage
                      </Button>
                      <Button
                        onClick={() => navigate(getCardLink())}
                        className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl"
                        data-testid="view-details-btn"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Voir les détails
                      </Button>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            </AnimatePresence>
          )}
        </div>

        {/* Disclaimer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-16 text-center"
        >
          <p className="text-gray-500 text-sm max-w-md mx-auto leading-relaxed">
            Ce tirage est un outil de réflexion personnelle. 
            Les cartes ne prédisent pas l'avenir mais offrent 
            des pistes de méditation.
          </p>
        </motion.div>
          </>
        )}
      </main>
    </div>
  );
};

export default MysteriaeRandomPage;
